import {
  TransitTaskCarCategoryLimitFormDto,
  type TransitTaskCarCategoryLimitFormInterface,
} from '@zaoblako/shared/modules/client/dtos/transit-task-car-category-limit/transit-task-car-category-limit-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { HelpdeskCheckerKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-checker-key.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';

import * as yup from 'yup';

/** Client create or update request body */
export interface ClientFormInterface {
  /** Client display name */
  name: string;
  /** Max resident cars to pass */
  residentCarLimit?: number;
  /** Counterparties belonging to this client */
  contragents?: ContragentFindInterface[];
  /** Filials belonging to this client */
  filials?: FilialFindInterface[];
  /** Directions belonging to this client */
  helpdeskTaskTypes?: HelpdeskTaskTypeFindInterface[];
  /** If `true`, transit tasks will always be zero-sum */
  freeTransitTask?: boolean;
  /** If `true`, a guest transit task can be created for this client from a camera event */
  isCreateTransitTask?: boolean;
  /** Transit task limits for this client by categories */
  transitTaskCarCategoryLimits?: TransitTaskCarCategoryLimitFormInterface[];
}

/** Client create or update request body - DTO schema */
export const ClientFormDto = SerdeDtoClass.create<ClientFormInterface>(ClientDtoKeysEnum.CLIENT_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  residentCarLimit: {
    optional: true,
    label: 'Лимит постоянных ТС, кол-во',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE)
      .integer(DtoMessageEnum.NOT_INTEGER),
  },
  contragents: {
    label: 'Контрагенты',
    optional: true,
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  filials: {
    label: 'Филиалы',
    optional: true,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  helpdeskTaskTypes: {
    label: 'Типы заявок HelpDesk',
    optional: true,
    checkers: [HelpdeskCheckerKeyEnum.HELPDESK_TASK_TYPE_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  freeTransitTask: {
    optional: true,
    label: 'Бесплатный гостевой проезд',
    serializer: booleanSchema,
  },
  isCreateTransitTask: {
    optional: true,
    label: 'Создание гостевой заявки из события',
    serializer: booleanSchema,
  },
  transitTaskCarCategoryLimits: {
    label: 'Лимиты гостевых ТС по категориям',
    nested: TransitTaskCarCategoryLimitFormDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
